import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import {
    BroadcastAudienceMember,
    ResolutionCenterService,
} from '../../resolution-center.service';

@Component({
    selector: 'app-manage-broadcast-members-modal',
    templateUrl: './manage-broadcast-members-modal.component.html',
    styleUrls: ['./manage-broadcast-members-modal.component.scss'],
    standalone: false,
})
export class ManageBroadcastMembersModalComponent implements OnInit {
    @Input() audienceKey = '';
    @Input() memberCount = 0;
    @Output() close = new EventEmitter<void>();

    members: BroadcastAudienceMember[] = [];
    searchQuery = '';
    currentPage = 1;
    lastPage = 1;
    total = 0;
    isLoading = false;
    isClosing = false;
    private searchDebounceTimer: ReturnType<typeof setTimeout> | null = null;

    constructor(
        private resolutionCenterService: ResolutionCenterService,
        private router: Router,
    ) {}

    ngOnInit(): void {
        void this.loadMembers();
    }

    onBackdropClick(event: MouseEvent): void {
        if (event.target === event.currentTarget) {
            this.onClose();
        }
    }

    onClose(): void {
        this.isClosing = true;
        setTimeout(() => {
            this.close.emit();
            this.isClosing = false;
        }, 180);
    }

    async loadMembers(): Promise<void> {
        if (!this.audienceKey) {
            this.members = [];
            return;
        }

        this.isLoading = true;
        const query = this.searchQuery.trim() || undefined;
        const { data, error } = await this.resolutionCenterService.getBroadcastMembers(
            this.audienceKey,
            query,
            this.currentPage,
        );

        this.isLoading = false;
        if (error || !data) {
            this.members = [];
            return;
        }

        this.members = data.data ?? [];
        this.currentPage = data.current_page;
        this.lastPage = data.last_page;
        this.total = data.total;
    }

    onSearchInput(): void {
        if (this.searchDebounceTimer) {
            clearTimeout(this.searchDebounceTimer);
        }
        this.searchDebounceTimer = setTimeout(() => {
            this.currentPage = 1;
            void this.loadMembers();
            this.searchDebounceTimer = null;
        }, 300);
    }

    onPageChange(page: number): void {
        if (page < 1 || page > this.lastPage) {
            return;
        }
        this.currentPage = page;
        void this.loadMembers();
    }

    getPaginationPages(): (number | string)[] {
        const pages: (number | string)[] = [];
        const totalPages = this.lastPage;
        const activePage = this.currentPage;

        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) {
                pages.push(i);
            }
            return pages;
        }

        pages.push(1);

        if (activePage > 3) {
            pages.push('...');
        }

        const start = Math.max(2, activePage - 1);
        const end = Math.min(totalPages - 1, activePage + 1);

        for (let i = start; i <= end; i++) {
            pages.push(i);
        }

        if (activePage < totalPages - 2) {
            pages.push('...');
        }

        pages.push(totalPages);
        return pages;
    }

    onPaginationPageClick(page: number | string): void {
        if (typeof page === 'number') {
            this.onPageChange(page);
        }
    }

    getInitials(name?: string): string {
        if (!name) {
            return '?';
        }
        const words = name.trim().split(/\s+/);
        if (words.length >= 2) {
            return (words[0].charAt(0) + words[words.length - 1].charAt(0)).toUpperCase();
        }
        return words[0].charAt(0).toUpperCase();
    }

    onOpenMemberProfile(member: BroadcastAudienceMember): void {
        if (!member?.id) {
            return;
        }
        window.open(`/user/user/details-mantis/${member.id}`, '_blank');
    }

    onOpenDirectChat(member: BroadcastAudienceMember): void {
        if (!member?.id) {
            return;
        }
        this.resolutionCenterService.openDirectChatRequest = { userId: member.id };
        this.router.navigate(['/client-messages']);
        this.onClose();
    }
}
